import { FC, useContext, useMemo } from 'react';

import { Button, TextArea, Label, BucketIcon } from '@smartshell/ui-kit';
import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

import { getConfig } from 'components';
import { LocalizationContext } from 'localization';

import { IFormValues } from '../types';
import { IPreviewProps } from './interfaces';
import s from './Preview.module.scss';
import { checkIntersection } from './utils';

export const Preview: FC<IPreviewProps> = ({
  hostGroupsTitlesById,
  normalizedHosts,
  currentBooking,
  cancelBooking,
  isUpdating,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    control,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<IFormValues>();

  const selectedHostsIds = watch('hostsIds');
  const duration = watch('duration');
  const startDate = watch('date');
  const startTime = watch('time');

  const isFilled =
    duration && startDate && startTime && selectedHostsIds.length;

  const bookingStart = useMemo(() => {
    if (!startDate || !startTime) return undefined;

    const [hours, minutes] = startTime.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return undefined;

    return dayjs(startDate).hour(hours).minute(minutes);
  }, [startDate, startTime]);

  const bookingEnd = useMemo(() => {
    if (!bookingStart || !duration) return undefined;

    return bookingStart.add(duration, 'hours');
  }, [bookingStart, duration]);

  const hasIntersections = useMemo(() => {
    if (!normalizedHosts || !bookingStart || !bookingEnd) return false;

    for (const id of selectedHostsIds) {
      if (!normalizedHosts[id]) continue;

      if (
        checkIntersection({
          bookings: normalizedHosts[id].bookings,
          currentBooking,
          startTime: bookingStart,
          endTime: bookingEnd,
        })
      ) {
        return true;
      }
    }

    return false;
  }, [
    selectedHostsIds,
    normalizedHosts,
    currentBooking,
    bookingStart?.toString(),
    bookingEnd?.toString(),
  ]);

  const hasDisabledHosts = useMemo(() => {
    if (!normalizedHosts || !bookingStart || !bookingEnd) return false;

    return selectedHostsIds.some((id) => {
      const host = normalizedHosts[id];
      if (!host) return false;

      const config = getConfig({
        host,
        startTime: bookingStart,
        endTime: bookingEnd,
        currentBooking,
        t,
      });

      return config.disabled;
    });
  }, [
    selectedHostsIds,
    normalizedHosts,
    bookingStart?.toString(),
    bookingEnd?.toString(),
    currentBooking,
    t,
  ]);

  return (
    <div className={s.preview}>
      <div className={s.hosts}>
        {selectedHostsIds.length ? (
          selectedHostsIds.map((hostId) => {
            const host = normalizedHosts?.[hostId];

            if (!host) return null;

            const hasIntersection = checkIntersection({
              bookings: host.bookings,
              startTime: dayjs(startDate),
              endTime: bookingEnd,
              currentBooking,
            });

            return (
              <div key={`preview-host-${hostId}`} className={s.host}>
                <div className={s['host-data']}>
                  <div className={s.alias}>
                    {host.alias}
                    {hasIntersection && (
                      <Label iconName="CalendarIcon" color="orange" />
                    )}
                  </div>
                  <span className={s.group}>
                    {hostGroupsTitlesById?.[host.group_id]}
                  </span>
                </div>

                <Button
                  variant="ghost"
                  onClick={() =>
                    setValue(
                      'hostsIds',
                      selectedHostsIds.filter((hostId) => hostId !== host.id)
                    )
                  }
                >
                  <BucketIcon color="secondary" size="s" />
                </Button>
              </div>
            );
          })
        ) : (
          <span className={s.placeholder}>
            {t('booking.preview.placeholder')}
          </span>
        )}
      </div>
      <div className={s.time}>
        <div className={s.row}>
          <span>{t('booking.add.startDate')}</span>
          {startDate ? (
            <div className={s['date-time']}>
              <span className={s.date}>
                {dayjs(startDate).format('DD.MM.YYYY')}
              </span>
              {bookingStart?.format('HH:mm')}
            </div>
          ) : (
            '—'
          )}
        </div>
        <div className={s.row}>
          <span>{t('booking.add.endDate')}</span>
          {bookingEnd ? (
            <div className={s['date-time']}>
              <span className={s.date}>{bookingEnd.format('DD.MM.YYYY')}</span>
              {bookingEnd?.format('HH:mm')}
            </div>
          ) : (
            '—'
          )}
        </div>
        <div className={s.row}>
          <span>{t('duration')}</span>
          {duration ? t('time.contracted.hours', duration) : '—'}
        </div>
      </div>
      <Controller
        render={({ field, fieldState: { error } }) => (
          <TextArea
            {...field}
            rows={3}
            title={t('booking.add.comment.title')}
            placeholder={t('booking.add.comment.placeholder')}
            errorWrapperClassName={s.comment}
            bordered
            error={error?.message}
            limit={256}
          />
        )}
        name="comment"
        control={control}
      />
      <div className={s.controls}>
        {currentBooking && (
          <Button
            color="red"
            onClick={cancelBooking}
            className={s['cancel']}
            variant="outline"
            disabled={isSubmitting || isUpdating}
            iconName="CancelIcon"
          />
        )}
        <Button
          type="submit"
          fullWidth
          disabled={
            !isFilled ||
            isSubmitting ||
            hasIntersections ||
            hasDisabledHosts ||
            isUpdating
          }
        >
          {t(currentBooking ? 'update' : 'bookings.add.submit')}
        </Button>
      </div>
    </div>
  );
};
